import { useState } from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { Button } from '../Button/Button'
import { getNavbarLinksData } from './data'
import { NavbarActions } from './NavbarActions'
import { NavbarLogo } from './NavbarLogo'
import { NavMenu } from './NavMenu'
import { WebsiteNavOverlay } from './NavOverlay'
import './styles.css'

import type { SliteBackgroundColor } from '../SlitePage'

export type WebsiteNavbarProps = {
  className?: string
  background?: SliteBackgroundColor
  /** Full-width bars (help center) center the menu on the page instead of
   * packing it next to the logo. */
  centeredMenu?: boolean
  inverted?: boolean
  onMenuOpenChange?: (open: boolean) => void
  /** Campaign landing pages: logo + this single CTA, no menu — paid traffic
   * gets one exit. */
  minimalCta?: { label: React.ReactNode; href: string; onClick?: () => void }
}

export const WebsiteNavbar = ({
  className,
  background = 'shade-100',
  centeredMenu = false,
  inverted = false,
  onMenuOpenChange,
  minimalCta,
}: WebsiteNavbarProps) => {
  const intl = useIntl()
  const [isBurgerMenuOpen, setIsBurgerMenuOpen] = useState(false)
  const setMenuOpen = (open: boolean) => {
    setIsBurgerMenuOpen(open)
    onMenuOpenChange?.(open)
  }
  const NAVBAR_LINKS_DATA = getNavbarLinksData(intl)

  if (minimalCta) {
    return (
      <div
        className={classNames(className, {
          'dark-mode navbar-inverted': inverted,
        })}
      >
        <div className="flexbox flexbox-space-between flexbox-align-center flexbox-column-gap-8 navbar">
          <NavbarLogo />
          <Button
            variant="primary"
            size="default"
            href={minimalCta.href}
            onClick={minimalCta.onClick}
          >
            {minimalCta.label}
          </Button>
        </div>
      </div>
    )
  }

  const navMenuItems = NAVBAR_LINKS_DATA.map((item) => ({
    id: 'id' in item ? item.id : undefined,
    title: item.title,
    url: item.url || undefined,
    columns: item.columns.length > 0 ? item.columns : undefined,
    secondaryColumns:
      'secondaryColumns' in item ? item.secondaryColumns : undefined,
  }))

  return (
    // `dark-mode` is the design-system color-scheme primitive that flips the
    // shade tokens (paired with `force-light-mode` on the panels). `navbar-inverted`
    // is our own marker carrying the navbar-specific overrides (hover pills,
    // secondary border) so they don't piggyback on the generic primitive.
    <div
      className={classNames(className, {
        'dark-mode navbar-inverted': inverted,
      })}
    >
      <div
        className="flexbox flexbox-space-between flexbox-align-center flexbox-column-gap-8 navbar"
        style={centeredMenu ? { position: 'relative' } : undefined}
      >
        <NavbarLogo />

        <div
          className={classNames(
            'navbar-new-links flexbox flexbox-align-center flexbox-column-gap-24 margin-left-24',
            centeredMenu ? 'flexbox-justify-end' : 'flexbox-space-between'
          )}
          style={{ flex: 1 }}
        >
          <div
            style={
              centeredMenu
                ? {
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }
                : undefined
            }
          >
            <NavMenu items={navMenuItems} />
          </div>
          <NavbarActions />
        </div>

        <button
          type="button"
          className="navbar-new-menu-button border-none background-transparent"
          aria-label={intl.formatMessage(
            isBurgerMenuOpen
              ? { id: 'navbar.closeMenu', defaultMessage: 'Close menu' }
              : { id: 'navbar.openMenu', defaultMessage: 'Open menu' }
          )}
          aria-expanded={isBurgerMenuOpen}
          onClick={() => setMenuOpen(!isBurgerMenuOpen)}
        >
          <span
            className={classNames('menu-icon-new', {
              'menu-icon-new-open': isBurgerMenuOpen,
            })}
            aria-hidden="true"
          >
            <span className="menu-icon-new-bar" />
            <span className="menu-icon-new-bar" />
          </span>
        </button>
      </div>
      {isBurgerMenuOpen && <WebsiteNavOverlay background={background} />}
    </div>
  )
}
